import { Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import DataTable, { TableColumn } from "react-data-table-component";
// import CollapseFilterData from './CollapseFilterData';
import { useEffect, useMemo, useState } from 'react';
import { SearchTableButton } from '../../../../utils/Constant';
import api from '../../../../utils/api';
import Swal from 'sweetalert2';
import { FoodTableDataColumnType } from '../../../../Types/Application/Ecommerce/Ecommerce';
import FoodListFilterHeader from './FoodListFilterHeader';
import { FoodTableAction, FoodTableOfferComponent } from '../../../../Types/Application/Ecommerce/FoodList';
import { P } from '../../../../AbstractElements';
import { SVG } from '../../../../AbstractElements';


const FoodListContainer = () => {
  const [editData, setEditData] = useState<any>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [foodListTableDatas, setFoodListTableDatas] = useState<any[]>([]);

  // Pagination-related states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchFood = async (page: number, size: number) => {
    try {
      setLoading(true);

      const response = await api.get(
        `api/FoodMaster/GetAllFoodMasterPagination?pageNumber=${page}&pageSize=${size}`
      );
      if (response.status === 200) {
        setFoodListTableDatas(
          response.data.items.map((item: {
            foodMasterId: number;
            categoryName: string; foodname: string; sellingprice: string; foodprice: string; images: string; foodCategoryId: any;
          }) => ({
            foodMasterId: item.foodMasterId,
            categoryName: item.categoryName,
            foodname: item.foodname,
            sellingprice: item.sellingprice,
            foodprice: item.foodprice,
            images: item.images,
            foodCategoryId: item.foodCategoryId
          }))
        );

        // Set total record count from backend
        setTotalRows(response.data.totalCount);
      }
    } catch (error) {
      // console.error("Failed to fetch food:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFood(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/FoodMaster/DeleteFoodMasterById?FoodMasterId=${id}`);
      Swal.fire('Deleted!', 'Food has been deleted.', 'success');

      // Refresh current page data
      await fetchFood(currentPage, pageSize);
    } catch (error) {
      Swal.fire('Error!', 'Failed to delete the food.', 'error');
      console.error("Delete error:", error);
    }
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setEditModalOpen(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    if (!editData || !editData.foodMasterId || !editData.foodname || !editData.sellingprice || !editData.foodCategoryId) {
      Swal.fire("Error!", "Please fill all fields before saving.", "error");
      return;
    }
    if (isNaN(Number(editData.sellingprice)) || Number(editData.sellingprice) <= 0) {
      Swal.fire("Error!", "Please enter a valid selling price.", "error");
      return;
    }
    if (isNaN(Number(editData.foodprice)) || Number(editData.foodprice) <= 0) {
      Swal.fire("Error!", "Please enter a valid food price.", "error");
      return;
    }
    if (editData.foodname.trim() === "") {
      Swal.fire("Error!", "Food name cannot be empty.", "error");
      return;
    }
    try {
      await api.put(`/api/FoodMaster/UpdateFoodMasterById`, {
        foodMasterId: editData.foodMasterId,
        images: editData.images,
        sellingprice: editData.sellingprice,
        foodprice: editData.foodprice,
        foodname: editData.foodname,
        foodCategoryId: editData.foodCategoryId,
      });

      // Refresh current page data
      await fetchFood(currentPage, pageSize);

      setEditModalOpen(false);
      Swal.fire("Updated!", "Food has been updated.", "success");
    } catch (error) {
      Swal.fire("Error!", "Failed to update the food.", "error");
    }
  };

  const FoodListTableDataColumn: TableColumn<FoodTableDataColumnType>[] = [
    {
      name: "Food Name",
      cell: (row) => <FoodTableOfferComponent title={String(row.foodname)} />,
      sortable: true,
      grow: 2,
    },
    {
      name: "Food Category",
      cell: (row) => <FoodTableOfferComponent title={String(row.categoryName)} />,
      sortable: true,
      grow: 2,
    },
    {
      name: "Selling Price",
      cell: (row) => <p>{row.sellingprice}</p>,
    },
    {
      name: "Food Price",
      cell: (row) => <P>{row.foodprice}</P>,
    },
    {
      name: "Image",
      cell: (row) => (
        <div className="product-image">
          <img src={row.images} alt={row.foodname} className="img-fluid" />
        </div>
      ),
      grow: 1,
    },
    {
      name: "Action",
      cell: (row) =>
      (
        <>
          <span
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ cursor: "pointer" }}
          >
            <SVG
              iconId="edit-content"
              style={{ fill: "blue", width: 18, height: 18 }} />
          </span>
          <FoodTableAction foodMasterId={row.foodMasterId} onDelete={handleDelete} />
        </>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const filteredItems = foodListTableDatas.filter((item: any) =>
    Object.values(item)
      .join(' ')
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="dataTables_filter d-flex align-items-center">
        <Label className="me-2">{SearchTableButton}:</Label>
        <Input onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)} type="search" value={filterText} />
      </div>
    );
  }, [filterText]);

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div className="list-product-header">
                <FoodListFilterHeader />
                {/* <CollapseFilterData /> */}
              </div>
              <div className="list-product">
                <div className="table-responsive">
                  <DataTable
                    data={filteredItems}
                    columns={FoodListTableDataColumn}
                    striped
                    highlightOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={pageSize}
                    progressPending={loading}
                    selectableRows
                    subHeader
                    subHeaderComponent={subHeaderComponentMemo}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
      >
        <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
          Edit Category
        </ModalHeader>
        <ModalBody>
          {editData && (
            <>
              <Row>
                <Col sm="4">
                  <FormGroup>
                    <Label>Food Name</Label>
                    <Input
                      name="foodname"
                      type="text"
                      className="form-control"
                      value={editData.foodname}
                      onChange={handleEditChange}
                      placeholder="Enter food name here..."
                      required={true}
                    />
                  </FormGroup>
                </Col>
                <Col sm="4">
                  <FormGroup>
                    <Label>Food Selling Price</Label>
                    <Input
                      name="sellingprice"
                      className="form-control"
                      value={editData.sellingprice}
                      onChange={handleEditChange}
                      type="number"
                      placeholder="Enter food selling price here..."
                      required={true}
                    />
                  </FormGroup>
                </Col>
                <Col sm="4">
                  <FormGroup>
                    <Label>MRP</Label>
                    <Input
                      name="foodprice"
                      className="form-control"
                      value={editData.foodprice}
                      onChange={handleEditChange}
                      type="number"
                      placeholder="Enter MRP here..."
                    />
                  </FormGroup>
                </Col>
                <Col sm="4">
                  <FormGroup>
                    <Label>Image</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const result = reader.result as string;
                            // Remove data URL prefix if present
                            const base64 = result.includes(',') ? result.split(',')[1] : result;
                            setEditData((prev: any) => ({
                              ...prev,
                              images: base64
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleEditSave}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

export default FoodListContainer